import { processCluster } from './ClusteringAlgorithm';

/**
 * Map frontend keywords to backend format for clustering algorithm
 * @param {Array} keywords - Array of frontend keyword objects
 * @param {string} keywordSourceType - Type of keyword source (COMPETITOR, etc.)
 * @param {string} selectedSearchVolumeTimeframe - Selected timeframe for search volumes
 * @returns {Array} Array of backend-formatted keywords
 */
function mapToBackendFormat(keywords, keywordSourceType = null, selectedSearchVolumeTimeframe = 'LAST_KNOWN_VALUES') {
    return keywords.map(kw => {
        let searchVolume = 0;
        if (keywordSourceType === 'COMPETITOR') {
            searchVolume = kw.searchVolumesByTimeframe?.LAST_12_MONTHS || 0;
        } else if (selectedSearchVolumeTimeframe && kw.searchVolumesByTimeframe) {
            searchVolume = kw.searchVolumesByTimeframe[selectedSearchVolumeTimeframe] || 0;
        } else {
            searchVolume = kw.searchVolumesByTimeframe?.LAST_KNOWN_VALUES || 0;
        }
        
        return {
            _id: kw.id,
            kw: kw.keyword,
            sv: Math.max(searchVolume, 0) // NO_VALUE (-1) is no volume, not a negative one
        };
    });
}

/**
 * Get clusters for keywords with specified language and options
 * @param {Array} keywords - Array of keyword objects
 * @param {string} languageCode - Language code
 * @param {Object} options - Clustering options
 * @returns {Array} Array of cluster objects
 */
export function getClusters(keywords, languageCode = 'en', options = {}, keywordSourceType = null, selectedSearchVolumeTimeframe = 'LAST_KNOWN_VALUES') {
    const backendKeywords = mapToBackendFormat(keywords, keywordSourceType, selectedSearchVolumeTimeframe);
    return processCluster(backendKeywords, languageCode, options);
}

/**
 * Calculate cluster statistics from keywords
 * @param {Array} keywords - Array of frontend keyword objects
 * @returns {Object} Object with keywords count, searches, and difficulty
 */
function calculateClusterStats(keywords, keywordSourceType = null, selectedSearchVolumeTimeframe = 'LAST_KNOWN_VALUES') {
    if (!keywords || keywords.length === 0) {
        return { keywords: 0, searches: 0, difficulty: null, avgSearchVolume: 0 };
    }

    const totalSearches = keywords.reduce((sum, kw) => {
        let searchVolume = 0;
        if (keywordSourceType === 'COMPETITOR') {
            searchVolume = kw.searchVolumesByTimeframe?.LAST_12_MONTHS || 0;
        } else if (selectedSearchVolumeTimeframe && kw.searchVolumesByTimeframe) {
            searchVolume = kw.searchVolumesByTimeframe[selectedSearchVolumeTimeframe] || 0;
        } else {
            searchVolume = kw.searchVolumesByTimeframe?.LAST_KNOWN_VALUES || 0;
        }
        return sum + Math.max(searchVolume, 0); // NO_VALUE (-1) adds nothing
    }, 0);
    
    const keywordsWithKD = keywords.filter(kw => kw.rank && kw.rank >= 1 && kw.rank <= 100);
    const avgDifficulty = keywordsWithKD.length > 0
        ? Math.round(keywordsWithKD.reduce((sum, kw) => sum + kw.rank, 0) / keywordsWithKD.length)
        : null;

    return {
        keywords: keywords.length,
        searches: totalSearches,
        difficulty: avgDifficulty,
        avgSearchVolume: keywords.length > 0 ? Math.round(totalSearches / keywords.length) : 0
    };
}

/**
 * Get clusters with comprehensive data including total stats
 * @param {Array} keywords - Array of frontend keyword objects
 * @param {string} languageCode - Language code
 * @param {Object} options - Clustering options
 * @returns {Object} Object with clusters, totalStats, and performance timing
 */
export function getClustersWithStats(keywords, languageCode = 'en', options = {}, keywordSourceType = null, selectedSearchVolumeTimeframe = 'LAST_KNOWN_VALUES') {
    const startTime = performance.now();
    const backendKeywords = mapToBackendFormat(keywords, keywordSourceType, selectedSearchVolumeTimeframe);
    const clusters = processCluster(backendKeywords, languageCode, options);
    
    // Map results back to frontend format with stats
    const frontendClusters = clusters.map(cluster => {
        const clusterKeywordIds = backendKeywords
            .filter(kw => kw.cluster === cluster.name)
            .map(kw => kw._id);
        
        const clusterKeywords = keywords.filter(kw => clusterKeywordIds.includes(kw.id));
        const stats = calculateClusterStats(clusterKeywords, keywordSourceType, selectedSearchVolumeTimeframe);

        return {
            name: cluster.name,
            primaryKeyword: cluster.primaryKeyword,
            ...stats
        };
    });

    const totalStats = calculateClusterStats(keywords, keywordSourceType, selectedSearchVolumeTimeframe);
    const endTime = performance.now();

    return {
        clusters: frontendClusters,
        totalStats,
        performance: {
            duration: Math.round(endTime - startTime),
            keywordCount: keywords.length,
            clusterCount: frontendClusters.length
        }
    };
}

/**
 * Get clusters with keyword mappings for filtering
 * @param {Array} keywords - Array of keyword objects
 * @param {string} languageCode - Language code
 * @param {Object} options - Clustering options
 * @returns {Object} Object with clusters and keywordToCluster mapping
 */
export function getClustersWithMappings(keywords, languageCode = 'en', options = {}, keywordSourceType = null, selectedSearchVolumeTimeframe = 'LAST_KNOWN_VALUES') {
    const backendKeywords = mapToBackendFormat(keywords, keywordSourceType, selectedSearchVolumeTimeframe);
    const clusters = processCluster(backendKeywords, languageCode, options);

    // Create keyword-to-cluster mapping
    const keywordToCluster = new Map();
    backendKeywords.forEach(kw => {
        if (kw.cluster) {
            keywordToCluster.set(kw._id, kw.cluster);
        }
    });

    return {
        clusters,
        keywordToCluster
    };
}

/**
 * Calculate cluster statistics for visible keywords only
 * @param {Array} clusters - Array of cluster objects from clustering algorithm
 * @param {Array} visibleKeywords - Array of currently visible (filtered) keywords
 * @param {Map} keywordToCluster - Map of keyword ID to cluster name
 * @returns {Array} Array of clusters with recalculated statistics
 */
export function calculateClusterStatsForVisibleKeywords(clusters, visibleKeywords, keywordToCluster, keywordSourceType = null, selectedSearchVolumeTimeframe = 'LAST_KNOWN_VALUES') {
    // Create a map of visible keywords by cluster
    const visibleKeywordsByCluster = new Map();
    
    visibleKeywords.forEach(keyword => {
        const clusterName = keywordToCluster.get(keyword.id);
        if (clusterName) {
            if (!visibleKeywordsByCluster.has(clusterName)) {
                visibleKeywordsByCluster.set(clusterName, []);
            }
            visibleKeywordsByCluster.get(clusterName).push(keyword);
        }
    });

    // Recalculate statistics for each cluster based on visible keywords only
    return clusters.map(cluster => {
        const visibleClusterKeywords = visibleKeywordsByCluster.get(cluster.name) || [];
        const stats = calculateClusterStats(visibleClusterKeywords, keywordSourceType, selectedSearchVolumeTimeframe);

        return {
            ...cluster,
            ...stats
        };
    }).filter(cluster => cluster.keywords > 0); // Only show clusters that have visible keywords
}

/**
 * Get clusters with mappings and recalculated stats for visible keywords
 * @param {Array} allKeywords - All keywords (for clustering)
 * @param {Array} visibleKeywords - Currently visible (filtered) keywords
 * @param {string} languageCode - Language code
 * @param {Object} options - Clustering options
 * @returns {Object} Object with clusters (with recalculated stats) and keywordToCluster mapping
 */
export function getClustersWithStatsForVisibleKeywords(allKeywords, visibleKeywords, languageCode = 'en', options = {}, keywordSourceType = null, selectedSearchVolumeTimeframe = 'LAST_KNOWN_VALUES') {
    // Do clustering on ALL keywords to get consistent clusters
    const { clusters, keywordToCluster } = getClustersWithMappings(allKeywords, languageCode, options, keywordSourceType, selectedSearchVolumeTimeframe);
    
    // If no clusters were found, still return empty clusters array but with keywordToCluster map
    if (clusters.length === 0) {
        return {
            keywordToCluster: new Map() // Empty map when no clusters exist
        };
    }
    
    // Recalculate statistics based on visible keywords only
    const clustersWithStats = calculateClusterStatsForVisibleKeywords(clusters, visibleKeywords, keywordToCluster, keywordSourceType, selectedSearchVolumeTimeframe);
    
    // Calculate total stats for visible keywords
    const totalStats = calculateClusterStats(visibleKeywords, keywordSourceType, selectedSearchVolumeTimeframe);

    return {
        clusters: clustersWithStats,
        totalStats,
        keywordToCluster
    };
}

export default {
    getClusters,
    getClustersWithStats,
    getClustersWithMappings,
    calculateClusterStatsForVisibleKeywords,
    getClustersWithStatsForVisibleKeywords
};
